import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-xs hover:bg-primary/90",
        destructive:
          "border border-[var(--dash-neg,var(--semantic-error-500))] text-[color:var(--dash-neg,var(--semantic-error-500))] cursor-pointer shadow-xs hover:bg-[var(--dash-neg-soft,var(--semantic-error-100))] active:scale-[.97] motion-reduce:active:scale-100",
        destructiveFilled:
          "bg-semantic-error-500 text-white cursor-pointer shadow-xs hover:bg-semantic-error-600 active:scale-[.97] motion-reduce:active:scale-100",
        destructiveLink:
          "border-0 text-[color:var(--dash-neg,var(--semantic-error-500))] cursor-pointer hover:bg-[var(--dash-neg-soft,var(--semantic-error-100))] active:scale-[.97] motion-reduce:active:scale-100",
        outline:
          "border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-tint-secondary dark:border-input dark:hover:bg-input/50",
        primary:
          "bg-primary-500 text-white shadow-xs hover:bg-primary-600 cursor-pointer",
        secondary:
          "bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80",
        success:
          "bg-[var(--dash-kept,var(--semantic-success-500))] text-white shadow-xs hover:bg-[var(--dash-kept,var(--semantic-success-500))]/90 cursor-pointer",
        ghost:
          "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        icon: "size-9",
        "icon-sm": "size-8",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot : "button"

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
